import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Alert
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  KeyboardArrowDown,
  KeyboardArrowUp
} from "@mui/icons-material";
import { IN8NWorkflowExecution, IN8NWorkflow } from "../../../../models/n8n-workflow";
import { formatDuration, formatLastExecuted } from "../../utils/helpers";
import useAxios from "../../../../components/UseAxios/useAxios";
import N8NWorkflowService from "../../../../services/api/n8n-workflow.service";

interface ExecutionHistoryProps {
  workflows: IN8NWorkflow[];
}

type StatusFilter = "all" | "success" | "failed" | "running";

const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({ workflows }) => {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [executions, setExecutions] = useState<IN8NWorkflowExecution[]>([]);

  const getExecutionsAxios = useAxios<IN8NWorkflowExecution[]>({ loading: "OnRequest" });

  useEffect(() => {
    if (selectedWorkflowId && selectedWorkflowId !== "all") {
      getExecutionsAxios.request(
        N8NWorkflowService.getExecutions(selectedWorkflowId)
      );
    } else {
      // If "all" is selected, we would need a different API endpoint
      // For now, we'll just clear the executions
      setExecutions([]);
    }
  }, [selectedWorkflowId]);

  useEffect(() => {
    if (getExecutionsAxios.isSuccess && getExecutionsAxios.data) {
      setExecutions(getExecutionsAxios.data);
    }
  }, [getExecutionsAxios.isSuccess, getExecutionsAxios.data]);

  const handleWorkflowChange = (event: any) => {
    setSelectedWorkflowId(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowClick = (executionId: string) => {
    setExpandedRow(expandedRow === executionId ? null : executionId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle fontSize="small" />;
      case "failed":
        return <ErrorIcon fontSize="small" />;
      case "running":
        return <HourglassEmpty fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "info" | "default" => {
    switch (status) {
      case "success":
        return "success";
      case "failed":
        return "error";
      case "running":
        return "info";
      default:
        return "default";
    }
  };

  const getWorkflowName = (workflowId: string): string => {
    const workflow = workflows.find(w => w._id === workflowId);
    return workflow?.name || "Unknown Workflow";
  };

  const calculateDuration = (startedAt: string, finishedAt?: string): string => {
    if (!finishedAt) return "In progress";

    const start = new Date(startedAt).getTime();
    const end = new Date(finishedAt).getTime();
    const durationMs = end - start;

    return formatDuration(durationMs);
  };

  // Filter executions based on status filter
  const filteredExecutions = executions.filter(execution => {
    if (statusFilter === "all") return true;
    return execution.status === statusFilter;
  });

  // Paginate filtered executions
  const paginatedExecutions = filteredExecutions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (getExecutionsAxios.isLoading && executions.length === 0) {
    return (
      <Box p={3}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box p={2} display="flex" gap={2} alignItems="center">
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Select Workflow</InputLabel>
          <Select
            value={selectedWorkflowId}
            onChange={handleWorkflowChange}
            label="Select Workflow"
          >
            <MenuItem value="all">All Workflows</MenuItem>
            {workflows.map((workflow) => (
              <MenuItem key={workflow._id} value={workflow._id}>
                {workflow.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" gap={1}>
          <Chip
            label="All"
            onClick={() => handleStatusFilterChange("all")}
            color={statusFilter === "all" ? "primary" : "default"}
            variant={statusFilter === "all" ? "filled" : "outlined"}
          />
          <Chip
            label="Success"
            onClick={() => handleStatusFilterChange("success")}
            color={statusFilter === "success" ? "success" : "default"}
            variant={statusFilter === "success" ? "filled" : "outlined"}
          />
          <Chip
            label="Failed"
            onClick={() => handleStatusFilterChange("failed")}
            color={statusFilter === "failed" ? "error" : "default"}
            variant={statusFilter === "failed" ? "filled" : "outlined"}
          />
          <Chip
            label="Running"
            onClick={() => handleStatusFilterChange("running")}
            color={statusFilter === "running" ? "info" : "default"}
            variant={statusFilter === "running" ? "filled" : "outlined"}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Workflow Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Started At</TableCell>
              <TableCell>Finished At</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedWorkflowId === "all" ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box py={4}>
                    <Typography variant="body2" color="text.secondary">
                      Please select a workflow to view execution history
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedExecutions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box py={4}>
                    <Typography variant="body2" color="text.secondary">
                      {statusFilter === "all"
                        ? "No execution history found for this workflow"
                        : `No ${statusFilter} executions found`}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedExecutions.map((execution) => (
                <React.Fragment key={execution._id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowClick(execution._id)}
                      >
                        {expandedRow === execution._id ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {getWorkflowName(execution.workflowId)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={getStatusIcon(execution.status) || undefined}
                        label={execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
                        size="small"
                        color={getStatusColor(execution.status)}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatLastExecuted(execution.startedAt)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {execution.finishedAt
                          ? formatLastExecuted(execution.finishedAt)
                          : "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {calculateDuration(execution.startedAt, execution.finishedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Expandable Row */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expandedRow === execution._id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          {execution.error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Error Details
                              </Typography>
                              <Typography variant="body2">
                                {execution.error}
                              </Typography>
                            </Alert>
                          )}

                          {execution.result && (
                            <Box
                              sx={{
                                backgroundColor: "#f5f5f5",
                                borderRadius: 1,
                                padding: 2
                              }}
                            >
                              <Typography variant="subtitle2" gutterBottom>
                                Execution Result
                              </Typography>
                              <pre style={{
                                margin: 0,
                                fontSize: "12px",
                                overflow: "auto",
                                maxHeight: "200px"
                              }}>
                                {JSON.stringify(execution.result, null, 2)}
                              </pre>
                            </Box>
                          )}

                          {!execution.error && !execution.result && (
                            <Typography variant="body2" color="text.secondary">
                              No additional details available
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {filteredExecutions.length > 0 && (
        <TablePagination
          component="div"
          count={filteredExecutions.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
        />
      )}
    </Box>
  );
};

export default ExecutionHistory;
